import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AutoComplete, Form, Input, InputNumber, Modal, Switch, Upload, message } from "antd";
import { LoadingOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Editor } from "@tinymce/tinymce-react";
import { uploadPhotoLibrary } from "../../api/PhotoLibrary";
import { textToHtml } from "../../utils/html";
import {
    createProduct,
    updateProduct,
    firstErrorMessage,
    type ProductItem,
    type ProductPayload,
} from "../../api/ProductAdmin";

/** Gợi ý đơn vị tính hay dùng; vẫn cho nhập tự do */
const UNIT_OPTIONS = ["Hộp", "Chai", "Lọ", "Tuýp", "Gói", "Cái", "Bộ", "Miếng"].map(value => ({ value }));
const MAX_IMAGE_MB = 5;
const FORM_FIELDS = ["code", "code_2", "name", "unit", "price", "discount_price", "avatar", "priority", "status", "description"];

const moneyFormatter = (value?: string | number) => `${value ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const moneyParser = (value?: string) => (value ? value.replace(/\./g, '') : '') as any;

/** Ô mô tả dạng trình soạn thảo, dùng được trong Form.Item (nhận value / onChange, lưu HTML) */
function DescriptionEditor({ value, onChange, placeholder }: { value?: string; onChange?: (v: string) => void; placeholder?: string }) {
    const dark = document.documentElement.classList.contains("dark");
    // init chỉ đọc một lần lúc dựng editor → giữ onChange mới nhất trong ref
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    return (
        <Editor
            // Bản self-host (GPL) trong public/tinymce — không cần API key cloud
            tinymceScriptSrc={`${process.env.PUBLIC_URL || ""}/tinymce/tinymce.min.js`}
            licenseKey="gpl"
            value={value || ""}
            onEditorChange={v => onChange?.(v)}
            init={{
                height: 260,
                menubar: false,
                branding: false,
                resize: false,
                skin: dark ? "oxide-dark" : "oxide",
                content_css: dark ? "dark" : "default",
                plugins: ["link", "lists", "table", "code", "fullscreen", "preview"],
                toolbar:
                    "undo redo | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright | bullist numlist | link table | preview fullscreen code",
                placeholder,
                paste_data_images: false,
                setup: (editor: any) => {
                    // tinymce-react chỉ đồng bộ khi keyup/change — bàn phím điện thoại không có keyup, thêm input để không mất ký tự cuối
                    editor.on("input", () => onChangeRef.current?.(editor.getContent()));
                },
            }}
        />
    );
}

interface Props {
    open: boolean;
    /** null = thêm mới */
    product: ProductItem | null;
    onClose: () => void;
    onSaved: () => void;
}

export default function ProductFormModal({ open, product, onClose, onSaved }: Props) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const avatar: string | undefined = Form.useWatch("avatar", form);

    useEffect(() => {
        if (!open) return;
        form.resetFields();
        form.setFieldsValue(product ? {
            code: product.code,
            code_2: product.code_2 || undefined,
            name: product.name,
            unit: product.unit || undefined,
            price: Number(product.price),
            discount_price: product.discount_price ? Number(product.discount_price) : undefined,
            avatar: product.avatar || undefined,
            priority: product.priority ?? 0,
            status: product.status === 0,
            // Mô tả cũ nhập bằng ô chữ thường: đổi xuống dòng thành đoạn, không thì editor gộp thành một dòng
            description: textToHtml(product.description || "") || undefined,
        } : { unit: "Hộp", priority: 0, status: true });
    }, [open, product, form]);

    const handleUpload = async (file: File) => {
        if (!file.type.startsWith("image/")) {
            message.error(t('products.image_invalid'));
            return;
        }
        if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
            message.error(t('products.image_too_large', { mb: MAX_IMAGE_MB }));
            return;
        }
        setUploading(true);
        try {
            const res = await uploadPhotoLibrary({ image: file, name: `product_${Date.now()}_${file.name}` });
            if (res.status && res.data?.image) {
                form.setFieldsValue({ avatar: res.data.image });
            } else {
                message.error(res.msg || t('products.upload_failed'));
            }
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async () => {
        let values: any;
        try {
            values = await form.validateFields();
        } catch {
            return; // lỗi validate đã hiện dưới từng field
        }

        // Field bỏ trống gửi null để khi sửa thì xóa được giá trị cũ
        const payload: ProductPayload = {
            code: values.code.trim(),
            code_2: values.code_2?.trim() || null,
            name: values.name.trim(),
            unit: values.unit?.trim() || undefined,
            price: values.price,
            discount_price: values.discount_price ?? null,
            avatar: values.avatar || null,
            priority: values.priority ?? 0,
            status: values.status ? 0 : 1,
            description: values.description?.trim() || null,
        };

        setSubmitting(true);
        try {
            const res = product ? await updateProduct(product.id, payload) : await createProduct(payload);
            if (!res.status) {
                // Gắn lỗi validate từ API vào đúng field (vd trùng mã SKU)
                if (res.error) {
                    form.setFields(Object.entries(res.error)
                        .filter(([name]) => FORM_FIELDS.includes(name))
                        .map(([name, errs]) => ({ name, errors: Array.isArray(errs) ? errs : [errs] })));
                }
                message.error(firstErrorMessage(res) || t(product ? 'products.update_failed' : 'products.add_failed'));
                return;
            }
            message.success(res.msg || t(product ? 'products.updated' : 'products.added'));
            onSaved();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={product ? t('products.modal_edit') : t('products.modal_add')}
            open={open}
            onCancel={onClose}
            onOk={handleSubmit}
            okText={t('products.save')}
            cancelText={t('products.cancel')}
            confirmLoading={submitting}
            okButtonProps={{ disabled: uploading }}
            width={760}
            className="dark:[&_.ant-modal-content]:bg-[#1f1f1f]
                dark:[&_.ant-modal-header]:bg-[#1f1f1f]
                dark:[&_.ant-modal-title]:text-white"
        >
            <Form layout="vertical" form={form} className="mt-4">
                <div className="flex flex-col sm:flex-row gap-x-4">
                    {/* ẢNH ĐẠI DIỆN — URL lưu ở field ẩn, ảnh upload qua thư viện ảnh */}
                    <Form.Item name="avatar" hidden><Input /></Form.Item>
                    <Form.Item label={t('products.label_avatar')} className="shrink-0">
                        <div className="relative w-[120px] h-[120px] group">
                            <Upload
                                listType="picture-card"
                                accept="image/*"
                                showUploadList={false}
                                beforeUpload={(file) => { handleUpload(file); return false; }}
                                className="[&_.ant-upload-select]:!w-[120px] [&_.ant-upload-select]:!h-[120px] [&_.ant-upload-select]:!m-0"
                            >
                                {avatar && !uploading ? (
                                    <img src={avatar} alt="" className="w-full h-full object-cover rounded-lg" />
                                ) : (
                                    <div className="text-gray-500 text-sm">
                                        {uploading ? <LoadingOutlined /> : <PlusOutlined />}
                                        <div className="mt-1">{t('products.upload')}</div>
                                    </div>
                                )}
                            </Upload>
                            {avatar && !uploading && (
                                <button
                                    type="button"
                                    title={t('products.remove_image')}
                                    onClick={() => form.setFieldsValue({ avatar: undefined })}
                                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white text-xs
                                        flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <DeleteOutlined />
                                </button>
                            )}
                        </div>
                    </Form.Item>

                    <div className="flex-1 min-w-0">
                        {/* TÊN */}
                        <Form.Item
                            label={t('products.label_name')}
                            name="name"
                            rules={[
                                { required: true, whitespace: true, message: t('products.name_required') },
                                { max: 255 },
                            ]}
                        >
                            <Input placeholder={t('products.placeholder_name')} maxLength={255} />
                        </Form.Item>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                            {/* MÃ SKU */}
                            <Form.Item
                                label={t('products.label_code')}
                                name="code"
                                rules={[{ required: true, whitespace: true, message: t('products.code_required') }]}
                            >
                                <Input placeholder="SP-001" maxLength={50} />
                            </Form.Item>

                            {/* MÃ VẠCH */}
                            <Form.Item label={t('products.label_barcode')} name="code_2">
                                <Input placeholder="893..." maxLength={100} />
                            </Form.Item>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4">
                    {/* GIÁ BÁN */}
                    <Form.Item
                        label={t('products.label_price')}
                        name="price"
                        rules={[{ required: true, message: t('products.price_required') }]}
                    >
                        <InputNumber className="w-full" min={0} formatter={moneyFormatter} parser={moneyParser} suffix="đ" />
                    </Form.Item>

                    {/* GIÁ ƯU ĐÃI — không vượt giá bán */}
                    <Form.Item
                        label={t('products.label_discount_price')}
                        name="discount_price"
                        dependencies={["price"]}
                        rules={[({ getFieldValue }) => ({
                            validator(_, value) {
                                const price = getFieldValue("price");
                                if (value == null || price == null || value <= price) return Promise.resolve();
                                return Promise.reject(new Error(t('products.discount_over_price')));
                            },
                        })]}
                    >
                        <InputNumber className="w-full" min={0} formatter={moneyFormatter} parser={moneyParser} suffix="đ" />
                    </Form.Item>

                    {/* ĐƠN VỊ */}
                    <Form.Item label={t('products.label_unit')} name="unit">
                        <AutoComplete options={UNIT_OPTIONS} placeholder="Hộp" filterOption={false} />
                    </Form.Item>

                    {/* ƯU TIÊN */}
                    <Form.Item label={t('products.label_priority')} name="priority" tooltip={t('products.priority_hint')}>
                        <InputNumber className="w-full" min={0} precision={0} />
                    </Form.Item>
                </div>

                {/* MÔ TẢ */}
                <Form.Item label={t('products.label_description')} name="description">
                    <DescriptionEditor placeholder={t('products.placeholder_description')} />
                </Form.Item>

                {/* TRẠNG THÁI */}
                <Form.Item label={t('products.label_status')} name="status" valuePropName="checked" className="mb-0">
                    <Switch
                        checkedChildren={t('products.status_active')}
                        unCheckedChildren={t('products.status_inactive')}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
}
